// Direct-lane data access — the ONLY place that talks to the `direct_lanes` table.
//
// A direct lane re-homes a branch: instead of being sorted at its home (destination)
// hub, its packages travel direct from an origin hub to the branch over a date
// range (effective_date → end_date). This bypasses the destination hub's sort and
// changes the transit estimate (origin→branch distance, not via the home hub).

import { supabase } from "../../lib/supabase";
import type { DirectLane, LaneRouting } from "../../lib/types";

interface DirectLaneRow {
  id: string;
  account_id: string;
  origin_hub_id: string;
  branch_id: string;
  routing: string | null;
  feed_pct: number | null;
  customer_id: string | null;
  transit_days: number | null;
  active: boolean;
  effective_date: string | null;
  end_date: string | null;
  notes: string | null;
}

/** DB row -> app DirectLane. */
export function rowToDirectLane(row: DirectLaneRow): DirectLane {
  return {
    id: row.id,
    originHubId: row.origin_hub_id,
    branchId: row.branch_id,
    routing: (row.routing === "h2h" ? "h2h" : "direct") as LaneRouting,
    feedPct: row.feed_pct,
    customerId: row.customer_id ?? null,
    transitDays: row.transit_days,
    active: row.active,
    effectiveDate: row.effective_date ?? undefined,
    endDate: row.end_date ?? null,
    notes: row.notes ?? undefined,
  };
}

/** App DirectLane -> DB row fields. */
export function directLaneInputToRow(accountId: string, l: DirectLane) {
  return {
    account_id: accountId,
    origin_hub_id: l.originHubId,
    branch_id: l.branchId,
    routing: l.routing,
    feed_pct: l.feedPct ?? null,
    customer_id: l.customerId ?? null,
    transit_days: l.transitDays ?? null,
    active: l.active,
    effective_date: l.effectiveDate ?? new Date().toISOString().slice(0, 10),
    end_date: l.endDate ?? null,
    notes: l.notes ?? null,
  };
}

/** Is a direct lane in effect on a given date (active + within its date range)? */
export function isDirectActiveOnDate(l: DirectLane, date: string): boolean {
  if (!l.active) return false;
  if (l.effectiveDate && date < l.effectiveDate) return false;
  if (l.endDate && date > l.endDate) return false;
  return true;
}

/** Load every direct lane in the account. */
export async function listAccountDirectLanes(accountId: string): Promise<DirectLane[]> {
  const { data, error } = await supabase
    .from("direct_lanes")
    .select("*")
    .eq("account_id", accountId);
  if (error) throw error;
  return (data as DirectLaneRow[]).map(rowToDirectLane);
}

/**
 * Replace the direct-lane set for one origin hub × a set of branches (one
 * destination hub's branches): delete the existing rows for those (origin,
 * branch) pairs, then insert the provided ones (with their date ranges).
 */
export async function saveLaneDirects(
  accountId: string,
  originHubId: string,
  branchIds: string[],
  lanes: DirectLane[]
): Promise<void> {
  if (branchIds.length === 0) return;
  const { error: delErr } = await supabase
    .from("direct_lanes")
    .delete()
    .eq("account_id", accountId)
    .eq("origin_hub_id", originHubId)
    .in("branch_id", branchIds);
  if (delErr) throw delErr;

  const rows = lanes.map((l) => directLaneInputToRow(accountId, l));
  if (rows.length === 0) return;
  const { error } = await supabase.from("direct_lanes").insert(rows);
  if (error) throw error;
}

/** Revert a direct lane: stamp an end date so it expires (the record is kept). */
export async function revertDirectLane(accountId: string, id: string, endDate: string): Promise<void> {
  const { error } = await supabase
    .from("direct_lanes")
    .update({ end_date: endDate })
    .eq("account_id", accountId)
    .eq("id", id);
  if (error) throw error;
}

/** Permanently remove a direct-lane record. */
export async function deleteDirectLane(accountId: string, id: string): Promise<void> {
  const { error } = await supabase.from("direct_lanes").delete().eq("account_id", accountId).eq("id", id);
  if (error) throw error;
}
